import { MonitorSettingsBase, MonitorSettings, MonitorSettingsEntry } from './MonitorSettings';
import { StringUtils } from '../Utils/StringUtils';
import { ValueEncoder } from "../Utils/ValueEncoder";
import { MonitorType } from "../Core/MonitorType";
import { Guid, HashSet, List } from '../Utils';
import { TftpSettings } from '../Tftp/TftpSettings';


export class CommandScriptKeys {
	public static readonly StartupConfig = "startup-config";
	public static readonly RunningConfig = "running-config";
	public static readonly Syslog = "syslog";
}


export class MonitorSettings_ConfigEntry extends MonitorSettingsEntry {

	public Protocol: string;
	public get HasProtocol(): boolean {
		return !!this.Protocol;
	}

	public UsesProtocol(protocolName: string): boolean {
		if (!!protocolName && this.HasProtocol) {
			return (protocolName === this.Protocol);
		}
		else {
			return false;
		}
	}

	public Name: string;
	public get HasName(): boolean {
		return !!this.Name;
	}

	public get DisplayName(): string {
		if (this.HasName) {
			return this.Name;
		}
		else {
			return "<All>";
		}
	}

	constructor(configKey?: string) {
		super();

		this._Init();

		if (configKey) {
			this.Name = configKey;
		}
	}

	private _Init(): void {
		this.Protocol = "";
		this.Name = "";
	}

	public CopyConfig(other: MonitorSettings_ConfigEntry): void {
		if (!other)
			return;
		if (other == this)
			return;

		super.CopyConfig(other);

		this.Protocol = other.Protocol;
		this.Name = other.Name;
	}

	public Write(obj: any, context: any): void {
		obj.monitored_configs_entry = {};
		obj = obj.monitored_configs_entry;

		super.Write(obj, context);

		if (this.HasProtocol)
			obj.protocol_name = this.Protocol;

		if (this.HasName)
			obj.name = this.Name;
	}

	public Read(obj: any, context: any): void {
		if (!obj.monitored_configs_entry)
			throw new Error();
		obj = obj.monitored_configs_entry;

		super.Read(obj, context);

		if (obj.protocol_name !== undefined)
			this.Protocol = obj.protocol_name;

		if (obj.name !== undefined)
			this.Name = obj.name;
	}
}


export class MonitorSettings_Config extends MonitorSettings<MonitorSettings_ConfigEntry> {

	public DefaultProtocolName: string;
	public DefaultProtocolType: string;

	public GetStartupConfig: boolean;
	public GetRunningConfig: boolean;
	public GetSyslog: boolean;

	public RunCustomScript: boolean;
	public CustomScriptGUID: string;
	public CustomScript: string;

	public ExportConfigsToServer: boolean;
	public ConfigExportFolder: string;
	public CreateDateSubfolder: boolean;
	public CreateDeviceSubfolder: boolean;
	public ConfigSaveChangesOnly: boolean;

	public Keep: number;
	public KeepCommandPrompt: boolean;

	public ExportToPRTG: boolean;
	public PrtgServerSettings: string;

	public TftpSettings: TftpSettings;

	public UseAllIpAddresses: boolean;

	constructor() {
		super();

		this.MonitorType = MonitorType.Config;

		this.DefaultProtocolName = "";
		this.DefaultProtocolType = "";

		this.GetStartupConfig = true;
		this.GetRunningConfig = true;
		this.GetSyslog = false;

		this.RunCustomScript = false;
		this.CustomScriptGUID = "";
		this.CustomScript = "";

		this.ExportConfigsToServer = false;
		this.ConfigExportFolder = "";
		this.CreateDateSubfolder = false;
		this.CreateDeviceSubfolder = false;
		this.ConfigSaveChangesOnly = true;

		this.Keep = 10;
		this.KeepCommandPrompt = false;

		this.ExportToPRTG = true;
		this.PrtgServerSettings = "";

		this.TftpSettings = new TftpSettings();

		this.UseAllIpAddresses = true;
	}

	public get HasDefaultProtocolName(): boolean {
		return !!this.DefaultProtocolName;
	}

	public get HasDefaultProtocolType(): boolean {
		return !!this.DefaultProtocolType;
	}

	public get HasCustomScriptGUID(): boolean {
		return !!this.CustomScriptGUID;
	}

	public get HasCustomScript(): boolean {
		return !!this.CustomScript;
	}

	public get HasConfigExportFolder(): boolean {
		return !!this.ConfigExportFolder;
	}

	public get HasPrtgServerSettings(): boolean {
		return !!this.PrtgServerSettings;
	}

	public get HasTftpSettings(): boolean {
		return (!!this.TftpSettings && !this.TftpSettings.IsEmpty);
	}

	public get ConfigKeys(): List<string> {
		let result = new List<string>();

		if (this.GetStartupConfig) {
			result.Add(CommandScriptKeys.StartupConfig);
		}
		if (this.GetRunningConfig) {
			result.Add(CommandScriptKeys.RunningConfig);
		}
		if (this.GetSyslog) {
			result.Add(CommandScriptKeys.Syslog);
		}
		if (this.RunCustomScript) {
			var commandScript = new CommandScript(this.CustomScript);
			var captureKeys = commandScript.CaptureKeys;
			captureKeys.ForEach((key) => result.Add(key));
		}

		return result;
	}

	public get MonitorRollup(): boolean {
		if (this.Count <= 0) {
			return true;
		}
		return (!!this.Find_Name(""))
	}

	protected get ObjSerEntriesName(): string {
		return "monitored_configs";
	}

	protected CreateEntry(): MonitorSettings_ConfigEntry {
		return new MonitorSettings_ConfigEntry();
	}

	public Add_Name(configName: string): MonitorSettings_ConfigEntry {
		if (!configName) {
			return null;
		}

		let configEntry = this.Find_Name(configName);
		if (!configEntry) {
			configEntry = new MonitorSettings_ConfigEntry(configName);
			this.Add(configEntry);
		}

		return configEntry;
	}

	public Add_Names(): number {
		let count = 0;

		if (this.GetStartupConfig) {
			this.Add_Name(CommandScriptKeys.StartupConfig);
		}
		if (this.GetRunningConfig) {
			this.Add_Name(CommandScriptKeys.RunningConfig);
		}
		if (this.GetSyslog) {
			this.Add_Name(CommandScriptKeys.Syslog);
		}
		if (this.RunCustomScript) {
			var commandScript = new CommandScript(this.CustomScript);
			var captureKeys = commandScript.CaptureKeys;
			captureKeys.ForEach((key) => this.Add_Name(key));
		}

		return count;
	}

	public Contains_Name(configName: string): boolean {
		if (!configName) {
			return false;
		}

		return (!!this.Find_Name(configName))
	}

	public CopySettings(other: MonitorSettingsBase): void {
		if (!other)
			return;

		if (other == this)
			return;

		let otherSettings = other as MonitorSettings_Config;
		if (!otherSettings)
			return;

		super.CopySettings(other);

		this.DefaultProtocolName = otherSettings.DefaultProtocolName;
		this.DefaultProtocolType = otherSettings.DefaultProtocolType;

		this.GetStartupConfig = otherSettings.GetStartupConfig;
		this.GetRunningConfig = otherSettings.GetRunningConfig;
		this.GetSyslog = otherSettings.GetSyslog;

		this.RunCustomScript = otherSettings.RunCustomScript;
		this.CustomScriptGUID = otherSettings.CustomScriptGUID;
		this.CustomScript = otherSettings.CustomScript;

		this.ExportConfigsToServer = otherSettings.ExportConfigsToServer;
		this.ConfigExportFolder = otherSettings.ConfigExportFolder;
		this.CreateDateSubfolder = otherSettings.CreateDateSubfolder;
		this.CreateDeviceSubfolder = otherSettings.CreateDeviceSubfolder;
		this.ConfigSaveChangesOnly = otherSettings.ConfigSaveChangesOnly;

		this.Keep = otherSettings.Keep;
		this.KeepCommandPrompt = otherSettings.KeepCommandPrompt;

		this.ExportToPRTG = otherSettings.ExportToPRTG;
		this.PrtgServerSettings = otherSettings.PrtgServerSettings;

		this.TftpSettings = new TftpSettings();
		if (otherSettings.HasTftpSettings) {
			this.TftpSettings.Copy(otherSettings.TftpSettings);
		}

		this.UseAllIpAddresses = otherSettings.UseAllIpAddresses;
	}

	public Find_Name(configName: string): MonitorSettings_ConfigEntry {
		let configEntry: MonitorSettings_ConfigEntry = null;

		for (let i = 0; i < this.Count; ++i) {

			let entry: MonitorSettings_ConfigEntry = this.Get(i);

			if (!entry.HasName)
				continue;

			if (!StringUtils.CaseInsensitiveEquals(entry.Name, configName))
				continue;

			configEntry = entry;
			break;
		}

		return configEntry;
	}

	public Find(other: MonitorSettings_ConfigEntry): MonitorSettings_ConfigEntry {
		if (!other)
			return null;

		let configEntry: MonitorSettings_ConfigEntry = null;

		if (other.HasName) {
			configEntry = this.Find_Name(other.Name);
		}

		return configEntry;
	}

	public Remove_Name(configName: string): void {
		if (!configName) {
			return;
		}

		let configEntry = this.Find_Name(configName);
		if (configEntry) {
			this.Remove(configEntry);
		}
	}

	public Remove_Names(removeAll: boolean): void {

		let removeList = new List<MonitorSettings_ConfigEntry>;

		for (let i = 0; i < this.Count; ++i) {

			let entry: MonitorSettings_ConfigEntry = this.Get(i);

			if (!entry.HasName)
				continue;

			if (!removeAll) {
				if (StringUtils.CaseInsensitiveEquals(entry.Name, CommandScriptKeys.StartupConfig))
					continue;
				if (StringUtils.CaseInsensitiveEquals(entry.Name, CommandScriptKeys.RunningConfig))
					continue;
			}

			removeList.Add(entry);
		}

		for (let i = 0; i < removeList.Count; ++i) {

			let entry = removeList.Get(i);
			if (entry == null)
				continue;

			this.Remove(entry);
		}
	}

	public UsesProtocol(protocolName: string): boolean {
		if (!protocolName) {
			return false;
		}

		return this.Some((entry) => entry.UsesProtocol(protocolName));
	}

	public Write(obj: any, context: any): void {
		obj.monitor_settings = {};
		obj = obj.monitor_settings;

		super.Write(obj, context);

		if (this.HasDefaultProtocolName)
			obj.default_proto_name = this.DefaultProtocolName;
		if (this.HasDefaultProtocolType)
			obj.default_proto_type = this.DefaultProtocolType;

		obj.get_startup_config = ValueEncoder.EncodeBoolean(this.GetStartupConfig);
		obj.get_running_config = ValueEncoder.EncodeBoolean(this.GetRunningConfig);
		obj.get_syslog = ValueEncoder.EncodeBoolean(this.GetSyslog);

		obj.run_custom_script = ValueEncoder.EncodeBoolean(this.RunCustomScript);
		if (this.HasCustomScriptGUID)
			obj.custom_script_guid = this.CustomScriptGUID;
		if (this.HasCustomScript)
			obj.custom_script = this.CustomScript;

		obj.export_configs_to_server = ValueEncoder.EncodeBoolean(this.ExportConfigsToServer);
		if (this.HasConfigExportFolder)
			obj.config_export_folder = this.ConfigExportFolder;
		obj.create_date_subfolder = ValueEncoder.EncodeBoolean(this.CreateDateSubfolder);
		obj.create_device_subfolder = ValueEncoder.EncodeBoolean(this.CreateDeviceSubfolder);
		obj.config_save_changes_only = ValueEncoder.EncodeBoolean(this.ConfigSaveChangesOnly);

		obj.keep = ValueEncoder.EncodeInteger(this.Keep);
		obj.keep_cmd_prompt = ValueEncoder.EncodeBoolean(this.KeepCommandPrompt);

		obj.enable_export = ValueEncoder.EncodeBoolean(this.ExportToPRTG);
		if (this.HasPrtgServerSettings)
			obj.server_settings = this.PrtgServerSettings;

		if (this.HasTftpSettings)
			this.TftpSettings.Write(obj, context);

		obj.use_all_ips = ValueEncoder.EncodeBoolean(this.UseAllIpAddresses);
	}

	public Read(obj: any, context: any): void {
		if (!obj.monitor_settings)
			throw new Error();
		obj = obj.monitor_settings;

		super.Read(obj, context);

		if (obj.default_proto_name !== undefined) {
			this.DefaultProtocolName = obj.default_proto_name;
		}
		if (obj.default_proto_type !== undefined) {
			this.DefaultProtocolType = obj.default_proto_type;
		}
		if (obj.get_startup_config !== undefined) {
			this.GetStartupConfig = ValueEncoder.DecodeBoolean(obj.get_startup_config);
		}
		if (obj.get_running_config !== undefined) {
			this.GetRunningConfig = ValueEncoder.DecodeBoolean(obj.get_running_config);
		}
		if (obj.get_syslog !== undefined) {
			this.GetSyslog = ValueEncoder.DecodeBoolean(obj.get_syslog);
		}

		if (obj.run_custom_script !== undefined) {
			this.RunCustomScript = ValueEncoder.DecodeBoolean(obj.run_custom_script);
		}
		if (obj.custom_script_guid !== undefined) {
			this.CustomScriptGUID = obj.custom_script_guid;
		}
		if (obj.custom_script !== undefined) {
			this.CustomScript = obj.custom_script;
		}

		if (obj.export_configs_to_server !== undefined) {
			this.ExportConfigsToServer = ValueEncoder.DecodeBoolean(obj.export_configs_to_server);
		}
		if (obj.config_export_folder !== undefined) {
			this.ConfigExportFolder = obj.config_export_folder;
		}
		if (obj.create_date_subfolder !== undefined) {
			this.CreateDateSubfolder = ValueEncoder.DecodeBoolean(obj.create_date_subfolder);
		}
		// Account for corrected misspelling in discovery engine. 
		if (obj.config_device_subfolder !== undefined) {
			this.CreateDeviceSubfolder = ValueEncoder.DecodeBoolean(obj.config_device_subfolder);
		}
		if (obj.create_device_subfolder !== undefined) {
			this.CreateDeviceSubfolder = ValueEncoder.DecodeBoolean(obj.create_device_subfolder);
		}
		if (obj.config_save_changes_only !== undefined) {
			this.ConfigSaveChangesOnly = ValueEncoder.DecodeBoolean(obj.config_save_changes_only);
		}
		if (obj.keep !== undefined) {
			this.Keep = ValueEncoder.DecodeInteger(obj.keep);
		}
		if (obj.keep_cmd_prompt !== undefined) {
			this.KeepCommandPrompt = ValueEncoder.DecodeBoolean(obj.keep_cmd_prompt);
		}

		if (obj.enable_export !== undefined) {
			this.ExportToPRTG = ValueEncoder.DecodeBoolean(obj.enable_export);
		}
		if (obj.server_settings !== undefined) {
			this.PrtgServerSettings = obj.server_settings;
		}

		if (obj.tftp_settings !== undefined) {
			this.TftpSettings = new TftpSettings();
			this.TftpSettings.Read(obj, context);
		}

		if (obj.use_all_ips !== undefined) {
			this.UseAllIpAddresses = ValueEncoder.DecodeBoolean(obj.use_all_ips);
		}
	}

}

export class CommandScript {

	public CommandScriptText: string;
	public CaptureKeys: HashSet<string>;

	constructor(scriptText?: string) {
		this._Init();
		if (scriptText !== undefined) {
			this.BuildFromString(scriptText);
		}
	}

	private _Init(): void {
		this.CommandScriptText = "";
		this.CaptureKeys = new HashSet<string>;
		this.CaptureKeys.Clear();
	}

	private _ParseScript(scriptLine: string): Command {
		if (scriptLine == undefined)
			return null;
		if (scriptLine == "")
			return null;

		let captureKey = "";
		let scriptCommand = "";

		let startIndex = -1;
		let endIndex = -1;
		let startCommandIndex = -1;

		startIndex = scriptLine.indexOf("[");
		if (startIndex >= 0) {
			endIndex = scriptLine.indexOf("]", startIndex);
			if (endIndex > startIndex) {
				startCommandIndex = endIndex + 1;

				startIndex++;
				captureKey = scriptLine.substring(startIndex, endIndex).trim();

				scriptCommand = scriptLine.substring(startCommandIndex);
				scriptCommand = scriptCommand.trim();
			}
		}

		if (captureKey == "")
			return null;

		let newCommand = new Command();
		newCommand.CaptureKey = captureKey;
		newCommand.Value = scriptCommand;
		return newCommand;
	}

	public BuildFromString(scriptText: string): boolean {
		if (scriptText == undefined)
			return false;
		if (scriptText == "")
			return false;

		this.CaptureKeys.Clear();
		this.CommandScriptText = scriptText;

		const scriptLines = scriptText.split(/[\r\n]+/);
		if (scriptLines !== null) {

			let captureKey = "";
			let scriptCommand = "";

			for (const script of scriptLines) {
				if (script == undefined)
					continue;
				if (script == "")
					continue;

				let index = -1;
				let line = script.trim();

				if (line.length <= 0)
					continue;

				// COMMENT
				if (line[0] == '#')
					continue;

				index = line.indexOf("@login");
				if (index >= 0) {
					continue;
				}

				index = line.indexOf("@enable");
				if (index >= 0) {
					continue;
				}

				index = line.indexOf("[-]");
				if (index >= 0) {
					continue;
				}

				index = line.indexOf("[");
				if (index >= 0) {
					var command = this._ParseScript(line);
					if (command !== null) {
						this.CaptureKeys.Add(command.CaptureKey);
					}
					continue;
				}
			}
		}

		return (this.CaptureKeys.Count > 0);
	}
}

export class Command {

	public CaptureKey: string;
	public Value: string;
	constructor() {
		this._Init();
	}

	private _Init(): void {
		this.CaptureKey = "";
		this.Value = "";
	}
}

